/* eslint-disable max-len */
import { Inject } from '@angular/core';
import { oneLine } from 'common-tags';
import { ENV, IEnvironment } from 'env/config';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Dropdown, Result, TDropdown } from '../interfaces/helpers';
import { Endpoint, PageConfig } from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';

import { IVendorGroup, VendorGroup } from '../models/vendorGroup.model';
import { Page } from './page';

export class TeamPage extends Page<VendorGroup> {
  private header;

  constructor(
    @Inject(ENV) env: IEnvironment,
    { endpoint }: Endpoint,
    config: PageConfig
  ) {
    super(endpoint as string, PageTitles.vendorGroup, config);

    this.initializeColumns({
      keys: new VendorGroup(),
      hiddenFormFields: this.hiddenFormFields,
    });
  }

  get actions(): string[] {
    return [];
  }

  get hiddenFormFields(): string[] {
    return ['vendorName', 'author'];
  }

  getVendorQuery(vendorAccountName?: string) {
    const query = oneLine`SELECT
        Id,
        Name,
        Vendor__c,
        Rep_Count__c,
        CreatedDate,
        LastModifiedDate,
        Owner.Name,
        CreatedBy.Name,
        LastModifiedBy.Name
      FROM Vendor_Group__c  `;

    return vendorAccountName
      ? oneLine`${query} WHERE Vendor_Group__c = '${vendorAccountName}'`
      : query;
  }

  // WHY this exist?, this is the same as 'getVendorQuery()'
  getVendorByVANQuery(vendorAccountName?: string) {
    const query = oneLine`SELECT
        Id,
        Name,
        Vendor__c,
        Rep_Count__c,
        CreatedDate,
        LastModifiedDate,
        Owner.Name,
        CreatedBy.Name,
        LastModifiedBy.Name
      FROM Vendor_Group__c  `;

    return vendorAccountName
      ? oneLine`${query} WHERE Vendor__c = '${vendorAccountName}'`
      : query;
  }

  getVendorDropdownQuery(vendorAccountName?: string) {
    // DPD-1302 during assign territory, only display "ACTIVE" vendor group instead of all vendor group
    const query = oneLine`SELECT Vendor__c, Name FROM Vendor_Group__c WHERE Store_Code__c IN (SELECT ID FROM ICI_Code__c WHERE Active__c = TRUE) `;

    return vendorAccountName
      ? oneLine`${query} AND Vendor__c = '${vendorAccountName}' `
      : query;
  }

  getVendorDropdownById(id: string) {
    const query = `SELECT Id, Name FROM Vendor_Group__c `;
    return `${query} WHERE Name = '${id}'`;
  }

  getVendorById(id?: string) {
    const query = `SELECT Id,
    Name,
    Vendor__c,
    Rep_Count__c,
    CreatedDate,
    LastModifiedDate,
    Owner.Name,
    CreatedBy.Name,
    LastModifiedBy.Name FROM Vendor_Group__c `;

    return id ? `${query} WHERE Name = '${id}'` : query;
  }

  getVendorByFederatedId(ids?: string) {
    if (ids.indexOf(`'`) === -1) {
      ids = `'${ids}'`;
    }
    return `SELECT CPMS_ID__c,Name,Vendor_Group__c FROM Vendor_Group_Member__c WHERE CPMS_ID__c IN (${ids})  AND CPMS_ID__c NOT IN ('')`;
  }

  getVendorByVendorId(ids?: string) {
    return `SELECT Id,Name,CPMS_ID__c FROM Vendor_Group_Member__c WHERE Vendor_Group__c IN (${ids}) AND Status__c = 'Active' `;
  }

  getVendorByVendorMemberId(ids?: string) {
    return `SELECT Id,Name,Vendor_Group__c FROM Vendor_Group_Member__c WHERE Name IN (${ids})  AND CPMS_ID__c NOT IN ('')`;
  }

  getVendorSalesByVendorGroupId(ids?: string) {
    return `SELECT Id,Name,CPMS_ID__c FROM Vendor_Group_Member__c WHERE Vendor_Account_Name__c IN (${ids})  AND CPMS_ID__c NOT IN ('')`;
  }

  getVendorByVendorGroupId(ids?: string) {
    return `SELECT Id,Name FROM Vendor_Group__c WHERE Name IN (${ids})`;
  }

  getVendorMemberByVendorGroupId(vendorGroupId: string) {
    return `SELECT CPMS_ID__c,Federation_ID__c,Name FROM Vendor_Group_Member__c WHERE Vendor_Account_Name__c = '${vendorGroupId}'  AND CPMS_ID__c NOT IN ('')`;
  }

  fetch(queryString?, condition?): Observable<Result<VendorGroup>> {
    const vendorName = queryString?.vendorAccountName || '';
    let params = { q: this.getVendorQuery(vendorName) };

    if (condition) {
      if (condition.condition.indexOf('vendorGroupId') !== -1) {
        params = { q: this.getVendorById(condition.value) };
      } else if (condition.condition.indexOf('vendorAccountName') !== -1) {
        params = { q: this.getVendorByVANQuery(condition.value) };
      }
    }

    return this.queryData(params);
  }

  fetchDropdownData(): Observable<Dropdown[]> {
    const queryString = { q: this.getVendorDropdownQuery() };
    queryString.q = queryString.q.split(' ').join('+');

    return this.queryData(queryString).pipe(
      map((result) =>
        result.items
          .map((x) => ({
            value: x.vendorAccountName,
            text: x.vendorName,
            checked: true,
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      )
    );
  }

  fetchDropdownDataById(id: string): Observable<Dropdown[]> {
    const queryString = { q: this.getVendorDropdownById(id) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.queryData(queryString).pipe(
      map(({ items }) =>
        items
          .map((x) => ({
            value: x.vendorName.toString(),
            text: x.vendorName,
            checked: true,
            description: x.vendorGroupId,
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      )
    );
  }

  fetchDropdownDataByFederationId(
    memberId?: string,
    vendorGroupId?: string
  ): Observable<Dropdown[]> {
    if (!vendorGroupId && !memberId) {
      const emptyDropdown: Dropdown[] = [];
      return of(emptyDropdown);
    }

    const queryString = { q: this.getVendorByFederatedId(memberId) };
    queryString.q = queryString.q.split(' ').join('+');

    let vendorGroupRequest: Observable<Result<VendorGroup>>;
    const items: Dropdown[] = [];
    if (vendorGroupId) {
      const queryStringVG = { q: this.getVendorByVendorGroupId(vendorGroupId) };
      queryStringVG.q = queryStringVG.q.split(' ').join('+');
      vendorGroupRequest = this.queryDataVendorMember(queryStringVG);
    } else {
      vendorGroupRequest = of({ items: [], meta: null, links: null });
    }

    return vendorGroupRequest.pipe(
      switchMap((result: Result<VendorGroup>) => {
        items.push(
          ...result.items.map(
            (record) =>
              new TDropdown({
                value: record.id ? record.id.toString() : '',
                text: record.vendorName,
                checked: false,
                description: record.vendorGroupId,
              })
          )
        );
        return this.queryData(queryString);
      }),
      map((result) => {
        items.push(
          ...result.items.map(
            (item) =>
              new TDropdown({
                value: item.cpmsid,
                text: item.vendorName,
                checked: false,
                description: item.vendorGroupId,
              })
          )
        );
        return items;
      })
    );
  }

  fetchDropdownDataByVendorGroupId(id: string): Observable<Dropdown[]> {
    if (!id) {
      const emptyDropdown: Dropdown[] = [];
      return of(emptyDropdown);
    }

    const queryString = { q: this.getVendorByVendorGroupId(id) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.queryDataVendorMember(queryString.q).pipe(
      map(
        ({ items }) =>
          items.map((x) => ({
            value: x.id.toString(),
            text: x.vendorName,
            checked: false,
          })),
        catchError((error) => [])
      )
    );
  }

  fetchById(id: string): Observable<VendorGroup> {
    const queryString = { q: this.getVendorById(id) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.getItems(queryString, {}, true).pipe(
      map((result) => {
        if (result.items.length === 0) {
          return new VendorGroup();
        }

        return result.items[0];
      })
    );
  }

  fetchByVendorMemberId(id: string): Observable<Dropdown[]> {
    const queryString = { q: this.getVendorByVendorMemberId(`'${id}'`) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.queryDataVendorMember(queryString).pipe(
      map(
        ({ items }) =>
          items.map((x) => ({
            value: x.cpmsid,
            text: x.vendorName,
            checked: false,
            description: x.vendorGroupId,
          })),
        catchError((error) => [])
      )
    );
  }

  update(item: IVendorGroup): Observable<VendorGroup> {
    let updatedVendors: VendorGroup[];
    return this.items.pipe(
      take(1),
      switchMap((vendors) => {
        if (!vendors || vendors.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(vendors);
        }
      }),
      switchMap((vendors) => {
        const teamIndex = vendors.findIndex((i) => i.id === item.id);
        updatedVendors = [...vendors];
        const oldTeam = updatedVendors[teamIndex];
        updatedVendors[teamIndex] = new VendorGroup({
          id: oldTeam.id,
          vendorName: item.vendorName,
          vendorAccountName: item.vendorAccountName,
          repCount: item.repCount,
          createdBy: item.createdBy,
          createdAt: item.createdAt,
          modifiedBy: item.modifiedBy,
          modifiedAt: item.modifiedAt,
          federationId: item.federationId,
          vendorMemberId: item.id.toString(),
          cpmsid: item.cpmsid,
        });
        return this.updateItems(updatedVendors[teamIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedVendors);
      })
    );
  }

  fetchVendorMembersByVendorGroup(vendorGroup: string): Observable<Dropdown[]> {
    if (!vendorGroup) {
      const emptyDropdown: Dropdown[] = [];
      return of(emptyDropdown);
    }

    const queryString = { q: this.getVendorByVendorMemberId(vendorGroup) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.fetchById(vendorGroup).pipe(
      take(1),
      switchMap((result: VendorGroup) => {
        const data = `'${result.id}'`;

        const subQueryString = { q: this.getVendorByVendorId(data) };
        subQueryString.q = subQueryString.q.split(' ').join('+');

        return this.queryDataVendorMember(subQueryString);
      }),
      map(
        ({ items }) =>
          (items || [])
            .map((vendor) => ({
              value: vendor.cpmsid,
              text: vendor.vendorName,
              checked: false,
              description: vendor.vendorGroupId,
            }))
            .sort((a, b) => (a.text > b.text ? 1 : -1)),
        catchError((error) => [])
      )
    );
  }

  createFromDTO(result): VendorGroup {
    return VendorGroup.createFromDTO(result);
  }

  private queryData(queryString): Observable<Result<VendorGroup>> {
    return this.getItems<Result<VendorGroup>>(
      queryString,
      this.header,
      true
    ).pipe(
      tap((result: Result<VendorGroup>) => {
        this.pageItems.next(result.items);
      }),
      catchError((error) => {
        const items: VendorGroup[] = [];
        items.push(
          new VendorGroup({
            id: '',
            vendorName: '',
            vendorAccountName: '',
            repCount: 0,
            createdBy: '',
            createdAt: null,
            modifiedBy: '',
            modifiedAt: null,
            federationId: '',
            vendorMemberId: '',
            cpmsid: '',
          })
        );
        return of({ items, links: null, meta: null });
      })
    );
  }

  private queryDataVendorMember(queryString): Observable<Result<VendorGroup>> {
    return this.getItems<Result<VendorGroup>>(queryString, null, true).pipe(
      map(({ items, links, meta }) => {
        const changedItems = (items as VendorGroup[]).map(
          (item) =>
            new VendorGroup({
              id: item.id,
              vendorName: item.vendorName,
              vendorAccountName: '',
              repCount: 0,
              createdBy: '',
              createdAt: new Date(),
              modifiedBy: '',
              modifiedAt: new Date(),
              federationId: item.federationId,
              vendorGroupId: item.vendorGroupId,
              vendorMemberId: item.id.toString(),
              cpmsid: item.cpmsid,
            })
        );

        return {
          items: changedItems,
          links,
          meta,
        };
      }),
      tap((result: Result<VendorGroup>) => {
        this.pageItems.next(result.items);
      }),
      catchError((error) => {
        const items: VendorGroup[] = [];

        items.push(
          new VendorGroup({
            id: '',
            vendorName: '',
            vendorAccountName: '',
            repCount: 0,
            createdBy: '',
            createdAt: new Date(),
            modifiedBy: '',
            modifiedAt: new Date(),
            federationId: '',
            vendorGroupId: '',
            vendorMemberId: '',
            cpmsid: '',
          })
        );
        return of({ items, links: null, meta: null });
      })
    );
  }
}
